/**
 * Procedural sound effects via Web Audio — no audio files.
 * Lazily creates the AudioContext on first play (autoplay policy).
 */

export type Sfx = {
  playBreak: () => void;
  playPlace: () => void;
  playHit: () => void;
  playSwing: () => void;
  playEat: () => void;
  playLevelUp: () => void;
  playCraft: () => void;
  setVolume: (volume: number) => void;
  dispose: () => void;
};

export function createSfx(): Sfx {
  let ctx: AudioContext | null = null;
  let master: GainNode | null = null;
  let volume = 0.4;

  function ensureContext(): { ctx: AudioContext; master: GainNode } | null {
    if (!ctx) {
      try {
        ctx = new AudioContext();
        master = ctx.createGain();
        master.gain.value = volume;
        master.connect(ctx.destination);
      } catch {
        return null;
      }
    }
    if (ctx.state === "suspended") void ctx.resume();
    return master ? { ctx, master } : null;
  }

  function noiseBurst(duration: number, filterFreq: number, gain = 1) {
    const audio = ensureContext();
    if (!audio) return;
    const { ctx, master } = audio;

    const bufferSize = Math.floor(ctx.sampleRate * duration);
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
    }

    const source = ctx.createBufferSource();
    source.buffer = buffer;

    const filter = ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.value = filterFreq;
    filter.Q.value = 0.8;

    const g = ctx.createGain();
    g.gain.value = gain;

    source.connect(filter).connect(g).connect(master);
    source.start();
  }

  function tone(
    freqStart: number,
    freqEnd: number,
    duration: number,
    type: OscillatorType = "square",
    gain = 0.5,
    delay = 0
  ) {
    const audio = ensureContext();
    if (!audio) return;
    const { ctx, master } = audio;

    const t0 = ctx.currentTime + delay;
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(freqStart, t0);
    osc.frequency.exponentialRampToValueAtTime(Math.max(1, freqEnd), t0 + duration);

    const g = ctx.createGain();
    g.gain.setValueAtTime(gain, t0);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + duration);

    osc.connect(g).connect(master);
    osc.start(t0);
    osc.stop(t0 + duration);
  }

  return {
    playBreak() {
      noiseBurst(0.12, 900, 0.9);
      tone(180, 90, 0.1, "triangle", 0.3);
    },
    playPlace() {
      tone(320, 180, 0.07, "square", 0.25);
    },
    playHit() {
      tone(220, 70, 0.12, "sawtooth", 0.4);
      noiseBurst(0.06, 500, 0.4);
    },
    playSwing() {
      noiseBurst(0.1, 2200, 0.15);
    },
    playEat() {
      noiseBurst(0.06, 700, 0.4);
      noiseBurst(0.06, 600, 0.35);
      tone(500, 300, 0.08, "triangle", 0.15, 0.1);
    },
    playLevelUp() {
      tone(523, 523, 0.12, "square", 0.25);
      tone(659, 659, 0.12, "square", 0.25, 0.1);
      tone(784, 784, 0.2, "square", 0.3, 0.2);
    },
    playCraft() {
      tone(400, 600, 0.08, "square", 0.2);
      tone(600, 900, 0.1, "square", 0.2, 0.08);
    },
    setVolume(v: number) {
      volume = v;
      if (master) master.gain.value = v;
    },
    dispose() {
      void ctx?.close();
      ctx = null;
      master = null;
    },
  };
}
